import { createIcons, icons } from 'lucide';
import EmblaCarousel from 'embla-carousel'
import AutoScroll from 'embla-carousel-auto-scroll'
import { toggleMenu } from "./header";
import { scrollToHash, getSamePageAnchor } from "./smooth-scrolling";
import '../css/main.css';

document.addEventListener("DOMContentLoaded", () => {
  const mql = window.matchMedia('(max-width: 768px)');

  /**
   * Toggle del header
  */
  toggleMenu()

  /**
   * Infinite marquee
   */
  const marquees = document.querySelectorAll(".embla");
  if (marquees.length > 0) {
    marquees.forEach((container) => {
      handleMarquee(container);
    });
  }

  /**
   * Load Lucide icons
   */
  createIcons({ icons });

  /**
   * Store filter toggle
   */
  filterToggle();

  /**
   * Featured products carousel
   */
  const carousel = document.querySelector("#relatedProducts");
  if (carousel) {
    import("./carousel").then((module) => {
      module.carousel(carousel);
    });
  }

  /**
   * Smooth scrolling support
   */
  // If a link's href is within the current page, scroll to it instead
  document.querySelectorAll('a[href]').forEach(a => {
    a.addEventListener('click', e => {
      scrollToHash(getSamePageAnchor(a), e);
    });
  });
  // Scroll to the element in the URL's hash on load
  scrollToHash(window.location.hash);

  /**
   * ACF7 event listener
   */
  document.addEventListener('wpcf7mailsent', (event) => {
    gtag('event', 'Formulario', {
      'event_category': event.detail.contactFormId,
      'event_label': event.detail.unitTag
    });
  }, false);

  /**
   * Slider
   */
  const sliders = document.querySelectorAll(".slider");
  if (sliders.length > 0) {
    import("./slider").then((module) => {
      sliders.forEach((slider) => {
        module.slider(slider);
      });
    });
  }

  /**
   * Featured Products Block
   */
  const featuredProductsBlocks = document.querySelectorAll(".block__productos-destacados");
  if (featuredProductsBlocks.length > 0) {
    import("./featured-products").then((module) => {
      featuredProductsBlocks.forEach((block) => {
        module.featuredProducts(block);
      });
    });
  }

  /**
   * Archive title
   */
  const archiveTitleContainer = document.getElementById('archiveTitle');
  if (archiveTitleContainer) {
    import("./archive-title").then((module) => {
      module.handleArchiveTitle(archiveTitleContainer);
    });
  }

  /**
   * Product tabs
   */
  const productTabs = document.querySelectorAll(".product-tabs");
  if (productTabs.length > 0) {
    import("./single-product").then((module) => {
      productTabs.forEach((block) => {
        module.handleTabs(block);
      });
    });
  }

  /**
   * Reviews toggle
   */
  const productReviews = document.querySelectorAll("#reviewsWrapper");
  if (productReviews.length > 0) {
    import("./single-product").then((module) => {
      productReviews.forEach((block) => {
        module.handleReviewsForm(block);
      });
    });
  }

  /**
   * Scroll to reviews from the summary CTA
   */
  const scrollToReviewsLink = document.querySelector("#scrollToReviews");
  if (scrollToReviewsLink) {
    import("./single-product").then((module) => {
      module.handleScrollToReviews(scrollToReviewsLink);
    });
  }

  /**
   * Product dialogs (payment methods, shipping calculator)
   */
  const dialogButtons = document.querySelectorAll("[data-nl-dialog]");
  if (dialogButtons.length > 0) {
    import("./single-product").then((module) => {
      dialogButtons.forEach((button) => module.handleDialog(button));
    });
  }

  /**
   * Checkout: payment method sync for the bank transfer discount
   */
  if (document.querySelector(".wp-block-woocommerce-checkout") && window.wc?.blocksCheckout) {
    import("./checkout").then((module) => {
      module.syncPaymentMethod();
    });
  }

  /**
   * Comments list toggle
   */
  const commentsWrapper = document.querySelector("#commentsWrapper");
  if (commentsWrapper) {
    import("./single-product").then((module) => {
      module.handleCommentsList();
    });
  }

  /**
   * Search toggle functionality for desktop
   */
  import('./header-search').then((module) => {
    module.handleSearchToggle();
  });

  /**
   * Accordion functionality
   */
  const accordions = document.querySelectorAll('.accordion-wrapper');
  if (accordions.length > 0) {
    accordions.forEach((accordion) => {
      import('./accordion').then((module) => {
        module.handleAccordion(accordion);
      });
    });
  }
});

const filterToggle = () => {
  const filterToggleBtn = document.getElementById('filterToggle');
  const filtersContainer = document.getElementById('filters');
  const plusIcon = filterToggleBtn?.querySelector('[data-lucide="plus"]');

  if (!filterToggleBtn || !filtersContainer) return;

  // Initially hide the filters
  filtersContainer.style.opacity = '0';
  filtersContainer.style.visibility = 'hidden';
  filtersContainer.style.transform = 'translateY(-10px)';
  filtersContainer.style.transition = 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out, transform 0.3s ease-in-out';

  let isOpen = false;

  filterToggleBtn.addEventListener('click', (e) => {
    e.preventDefault();

    if (!isOpen) {
      // Show filters
      filtersContainer.style.visibility = 'visible';
      filtersContainer.style.opacity = '1';
      filtersContainer.style.transform = 'translateY(0)';

      // Change icon to minus
      if (plusIcon) {
        plusIcon.setAttribute('data-lucide', 'minus');
      }
    } else {
      // Hide filters
      filtersContainer.style.opacity = '0';
      filtersContainer.style.transform = 'translateY(-10px)';

      // Hide after animation completes
      setTimeout(() => {
        filtersContainer.style.visibility = 'hidden';
      }, 300);
    }

    isOpen = !isOpen;
  });

  // Close filters when clicking outside
  document.addEventListener('click', (e) => {
    if (!filterToggleBtn.contains(e.target) && !filtersContainer.contains(e.target) && isOpen) {
      // Hide filters
      filtersContainer.style.opacity = '0';
      filtersContainer.style.transform = 'translateY(-10px)';

      setTimeout(() => {
        filtersContainer.style.visibility = 'hidden';
      }, 300);
    }
  });
};

export const handleMarquee = (emblaNode) => {
  const emblaApi = EmblaCarousel(
    emblaNode,
    {
      loop: true,
      align: 'start',
      containScroll: 'trimSnaps', // evita “saltos” raros en bordes
      dragFree: false,             // movimiento continuo sin fricción de snaps
    },
    [
      AutoScroll({
        speed: 1.5,                 // ajusta a gusto (0.5 lento, 2 rápido)
        startDelay: 0,
        stopOnInteraction: false, // que no se frene al hover/drag
        stopOnMouseEnter: false,
      }),
    ]
  )
}